<template>
  <div class="invite-modal-content wt-modal-grow-animation">
    <div class="invite-modal-header">
      <i class="wticon wticon-cross3" @click="closeModal"></i>
    </div>

    <div class="invite-container">
      <invite-header :state="currentState.header" />
      <div class="invite-body">
        <keep-alive>
          <component
            :is="currentState.body"
            :handlers="{
              getUsers,
              addUser,
              changeUser,
              removeUser,
              onChangeData,
              getOrganizations,
              getProgrammers,
              isMaxUsers,
              productionLine,
            }"
          />
        </keep-alive>
      </div>
      <invite-footer
        :state="currentState.footer"
        :handlers="{
          changeCurrentState,
          action,
          isOnlyOneProgrammer,
          isMaxUsers,
          hasUsers,
          hasTempUsers,
          hasProductionLine,
          currentState,
          process,
          processDirty,
        }"
      />
      <div v-if="process" class="process-backdrop" />
    </div>
  </div>
</template>

<script>
/**
 *  Invite
 * */
import app from 'app';
import Vue from 'vue';
import Analytics from 'c/WtAnalytics';
import InviteHeader from './InviteHeader.vue';
import InviteFooter from './InviteFooter.vue';
import InviteBulk from './InviteBulk.vue';
import InviteLine from './InviteLine.vue';
import InviteProductionLine from './InviteProductionLine.vue';

const states = {
  invite: {
    name: 'invite',
    header: 'inviteColleagues',
    body: 'inviteLine',
    footer: {
      goToBulk: 'bulk',
      disabled: 'hasUsers',
    },
  },
  bulk: {
    name: 'bulk',
    header: 'inviteColleagues',
    body: 'inviteBulk',
    footer: {
      backTo: 'invite',
      disabled: 'hasTempUsers',
    },
  },
  production: {
    name: 'production',
    header: 'productionLine',
    body: 'inviteProductionLine',
    footer: {
      backTo: 'invite',
      disabled: 'hasProductionLine',
    },
  },
};

const MAX_USERS = 20;
export default Vue.extend({
  components: {
    InviteHeader,
    InviteFooter,
    InviteBulk,
    InviteLine,
    InviteProductionLine,
  },

  props: {
    closeModal: {
      type: Function,
    },
  },

  data: function () {
    return {
      currentStateName: 'invite',
      process: false,
      processDirty: false,
      modalSetTimeout: null,
      users: [],
      tempUsers: [],
      productionLine: [],
    };
  },

  computed: {
    currentState: function () {
      return states[this.currentStateName];
    },

    hasUsers: function () {
      return this.users.filter((user) => !_.isEmpty(user) && user.email).length;
    },

    hasTempUsers: function () {
      return this.tempUsers.filter((user) => !_.isEmpty(user) && user.email)
        .length;
    },

    hasProductionLine: function () {
      return this.productionLine.length;
    },

    isMaxUsers: function () {
      return this.users.length >= MAX_USERS;
    },
  },

  methods: {
    getProgrammers: function () {
      return app.programmers.map((programmer) => {
        return {
          id: programmer.get('id'),
          label: programmer.get('label'),
        };
      });
    },

    getOrganizations() {
      return tk.context.user.organizations;
    },

    isOnlyOneOrganization: function () {
      return this.getOrganizations().length === 1;
    },

    isOnlyOneProgrammer: function () {
      return this.getProgrammers().length === 1;
    },

    getUsers: function () {
      return this.users;
    },

    addUser: function (props) {
      if (this.users.length >= MAX_USERS) {
        return;
      }

      const user = Object.assign({}, props);

      this.users.push(user);
    },

    changeUser: function (index, prop, event) {
      const value = _.isDefined(event.clipboardData)
        ? event.clipboardData.getData('text')
        : event.target.value;

      this.users = this.users.map((user, i) => {
        if (index === i) {
          if (value === '') {
            delete user[prop];
          } else {
            user[prop] = value;
          }
        }
        return user;
      });
    },

    removeUser: function (index) {
      this.users = this.users.filter((user, i) => i !== index);
    },

    onChangeData: function (key, data) {
      this[key] = data;
    },

    action: function () {
      switch (this.currentStateName) {
        case 'invite': {
          const isNotValid = this.validateUsers();

          if (isNotValid) {
            return;
          }

          // if there's more then one programmer and no production lines chosen
          // or process is not dirty go to production line.
          if (
            (!this.isOnlyOneOrganization() || !this.isOnlyOneProgrammer()) &&
            (!this.hasProductionLine || !this.processDirty)
          ) {
            this.changeCurrentState('production');
            return;
          }

          this.inviteAction();
          break;
        }
        case 'production':
          this.inviteAction();
          break;
        case 'bulk':
          this.bulkAction();
          break;
      }
    },

    validateUsers: function (statuses) {
      const emailTempArr = [];

      this.users = this.users
        // add error property if email is invalid
        .map((user, index) => {
          const hasStatusError =
            statuses &&
            statuses.length &&
            statuses[index] &&
            statuses[index] !== 'VALID';

          // if user has email, check for client side errors
          if (user.email) {
            const userEmailLowerCase = user.email.trim().toLowerCase();
            const isEmailValid = app.namespace.Util.Validate.email(user.email);
            const isEmailExistAlready =
              emailTempArr.includes(userEmailLowerCase);

            if (!isEmailValid) {
              user.error = 'email';
            } else if (isEmailExistAlready) {
              user.error = 'duplicateEmail';
            } else {
              delete user.error;
            }

            emailTempArr.push(userEmailLowerCase);
          }

          // if statuses are passed, that's a server side errors.
          if (hasStatusError) {
            user.error = statuses[index];
          }

          return user;
        })
        // remove the users that don't have an email from list
        .filter((user) => user.email);

      // if some of the users has an error property.
      return this.users.some((user) => _.isDefined(user.error));
    },

    inviteAction: function () {
      this.process = true;

      app.api.invitations
        .post({
          invitations: this.users.map((user) => {
            user.email = user.email.trim().toLowerCase();
            return user;
          }),
          programmerIds: this.productionLine.length
            ? this.productionLine
            : this.getProgrammers().map((programmer) => programmer.id),
        })
        .done(() => {
          app.dialog
            .confirm({
              title: 'Invitations successfully sent!',
              description:
                'Your team members will receive their invitation within the next hour.',
              buttonYesText: 'OK',
            })
            .then(this.$props.closeModal);
        })
        .fail((err) => {
          console.error('Invite inviteAction: err:', err);

          // error handling
          switch (err.status) {
            case 422:
              this.validateUsers(err.statuses);
              this.changeCurrentState('invite');
              break;
            default:
              // for 400, 500 errors and etc.
              app.dialog.error({
                title: 'Sorry...',
                description:
                  'the invitations could not be sent. Please try again later.',
                showContactCsm: true,
              });
          }
        })
        .always((data) => {
          this.process = false;
          this.processDirty = true;
          Analytics.trigger('analytics:invitations:clickOnInvite', {
            Data: data,
          });
        });
    },

    bulkAction: function () {
      this.users = this.users
        .concat(this.tempUsers)
        .filter((user) => user.email)
        .slice(0, MAX_USERS);

      this.changeCurrentState('invite');
    },

    changeCurrentState: function (stateName) {
      if (!states[stateName]) {
        console.error(
          `Invite changeCurrentState: stateName is not in list! stateName: ${stateName}`
        );
        return;
      }

      this.currentStateName = stateName;
    },
  },
});
</script>

<style lang="scss">
$invite-modal-width: 815px;
$invite-modal-height: 596px;
$invite-modal-background: #ffffff;
$invite-modal-header: 45px;
$invite-height-100-minus-header: calc(100% - 45px);
$invite-modal-padding: 37px;

/* InviteModal component css */
.invite-modal-content {
  width: $invite-modal-width;
  height: $invite-modal-height;

  .invite-modal-header {
    width: 100%;
    display: flex;
    height: $invite-modal-header;
    background-color: #34404c;
    justify-content: flex-end;
    align-items: center;

    i {
      font-size: 20px;
      color: #fff;
      margin-right: 13px;
      cursor: pointer;
    }
  }

  /* Invite Container css */
  .invite-container {
    display: flex;
    flex-direction: column;
    padding-left: $invite-modal-padding;
    height: $invite-height-100-minus-header;
    width: 100%;
    background: $invite-modal-background;

    .process-backdrop {
      position: absolute;
      top: 145px; // 45px invite-modal-header + 100px invite-header
      left: 0;
      width: 100%;
      height: calc(100% - 145px);
      background-color: rgba(255, 255, 255, 0.5);
    }
  }

  /* Invite Header css */
  .invite-header {
    height: 100px;
    width: 100%;
    padding-right: $invite-modal-padding;

    > div {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      color: #4a4a4a;
      border-bottom: 1px solid #e6e6e6;
      height: inherit;
      width: inherit;
    }

    .title {
      font-size: 35px;
      font-weight: 500;
    }

    .description {
      font-size: 22px;
      font-weight: 300;
    }
  }

  /* Invite Body css */
  .invite-body {
    height: 364px;
  }

  /* Invite Footer css */
  .invite-footer {
    height: 87px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-right: $invite-modal-padding;

    .button-primary {
      width: 170px;
      height: 40px;
      border-radius: 2px;
      border: none;
      font-size: 18px;
      font-weight: 300;
    }

    .button-action {
      background-color: #20bc9c;
      transition: 0.2s background-color;
      color: #ffffff;
      margin-right: 17px;
      z-index: 2;

      &:disabled {
        background-color: #bdc3c7;
      }

      &:hover:enabled,
      &:active:enabled {
        background-color: #1ba085;
      }

      @keyframes rotate-circle {
        from {
          transform: rotate(0deg);
        }
        to {
          transform: rotate(360deg);
        }
      }

      &.process {
        position: relative;
        transition: 2s;

        &:before {
          content: '';
          background-color: #20bc9c;
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
        }

        &:after {
          content: '';
          width: 23px;
          height: 23px;
          z-index: 1;
          position: absolute;
          border: 2px solid #20bc9c;
          border-top: 2px solid #ffffff;
          border-right: 2px solid #ffffff;
          border-radius: 50%;
          top: 50%;
          left: 50%;
          margin-top: -10.5px;
          margin-left: -11.5px;
          animation: rotate-circle 1s infinite linear;
        }
      }
    }

    .button-back {
      border: 1px solid #4a4a4a;
      color: #39393a;
      background-color: transparent;
      transition: 0.2s background-color;
      margin-left: 12px;

      &:hover {
        color: #fff;
        border: 1px solid #4a4a4a;
        background-color: #4a4a4a;
      }

      &:active {
        color: #fff;
        border: 1px solid #333333;
        background-color: #333333;
      }
    }

    .invite-multiple {
      font-size: 15px;
      font-weight: 300;
      display: flex;
      justify-content: center;
      align-items: center;
      margin-top: 20px;
      margin-left: 13px;
      transition: visibility 0.2s;

      &.invite-multiple-hidden {
        visibility: hidden;
      }

      .invite-multiple-text {
        color: #898989;
        span {
          color: #1497f1;
          cursor: pointer;
        }
      }
    }
  }

  /* Invite Line css */
  .invite-line {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    position: relative;
    padding-left: 13px;

    .invite-line-box {
      width: 100%;
      overflow-y: auto;
      overflow-x: hidden;
      height: 100%;
      position: relative;
      margin-top: 12px;

      .invite-line-row {
        display: flex;
        flex-direction: column;
        width: 100%;
        margin-top: 20px;
        transition: all 0.5s;
        position: relative;

        &:first-child {
          margin-top: 0;
        }

        .invite-line-inputs {
          display: flex;

          input {
            height: 40px;
            border-radius: 2px;
            border: 1px solid #9b9b9b;
            padding-left: 15px;
            width: 170px;
            margin-right: 15px;
            transition: 0.5s border;
            font-size: 15px;
            font-weight: 300;

            &:hover,
            &:focus {
              border: 1px solid #06a9f4;
            }

            &:first-child {
              width: 343px;
            }
          }

          i {
            color: #9b9b9b;
            align-self: center;
            margin-right: 20px;
            cursor: pointer;
          }
        }

        .invite-line-error {
          color: #c30016;
          font-size: 13px;
          font-weight: 300;
          padding-left: 2px;
          position: absolute;
          bottom: -19px;
        }

        &.error-active {
          input[type='email'] {
            border: 1px solid #d0011b;
          }
        }
      }

      .list-enter,
      .list-leave-to,
      .list-leave-active {
        opacity: 0;
        transform: translateY(-30px);
      }
    }

    .invite-line-labels {
      display: flex;
      width: 100%;
      margin-top: 18px;

      label {
        color: #4a4a4a;
        font-size: 18px;
        font-weight: 500;
        width: 170px;
        margin-right: 15px;

        &:first-child {
          width: 343px;
        }
      }
    }

    .invite-more-invitations {
      position: absolute;
      bottom: -64px;
      right: 237px;
      height: 40px;
      border-radius: 2px;
      background-color: #fff;
      border: 1px solid #06a9f4;
      font-size: 18px;
      font-weight: 300;
      color: #06a9f4;

      &:hover:enabled {
        border: 1px solid #06a9f4;
        background-color: #06a9f4;
        color: #fff;
      }

      &:active:enabled {
        border: 1px solid #2980b9;
        background-color: #2980b9;
        color: #fff;
      }

      &:disabled {
        border: 1px solid #bdc3c7;
        background-color: #fff;
        color: #bdc3c7;
      }
    }
  }

  /* Invite Bulk css */
  .invite-bulk {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding-left: 8px;

    label {
      color: #4a4a4a;
      font-size: 18px;
      font-weight: 500;
      margin-top: 18px;
    }

    textarea {
      width: 725px;
      height: 219px;
      border-radius: 2px;
      margin-top: 5px;
      resize: none;
      border: 2px solid #e6e6e6;
      transition: 0.2s border-color;
      font-size: 15px;
      padding-left: 15px;
      padding-top: 15px;
      font-weight: 300;

      &:hover,
      &:focus {
        border-color: #06a9f4;
      }
    }
  }

  /* Invite Production Line css */
  .invite-production-line {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding-left: 8px;
    margin-top: 20px;

    .invite-production-title {
      display: flex;
      align-items: center;
      span {
        color: #4a4a4a;
        font-size: 18px;
        font-weight: 300;
      }
    }

    .invite-production-row {
      display: flex;
      flex-direction: column;
      margin-top: 7px;
      width: 100%;
      height: 85%;
      overflow-y: auto;
      overflow-x: hidden;

      .wt-checkbox {
        margin-bottom: 18px;

        .checkbox-box {
          width: 20px;
          height: 20px;
          border: #e6e6e6 1px solid;
        }

        label {
          color: #4a4a4a;
          font-size: 18px;
          font-weight: 300;
          margin-bottom: 0;

          input {
            opacity: 0;
            &:checked + span:after {
              width: 7px;
              height: 14px;
              top: 12px;
              left: -25px;
              border-color: #1eaaf1;
            }
          }

          span {
            color: #39393a;
            font-size: 15px;
            font-weight: 300;

            &.bolder {
              font-weight: 500;
            }
          }
        }
      }
    }
  }
}
</style>
